import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../../components/SEO/SEOHead';

const FeaturesPage = () => {
  return (
    <>
      <SEOHead 
        title="Fleet Management Features - Complete TMS Solution Saudi Arabia"
        description="Advanced fleet management features: intelligent shipment tracking, automated costing, fuel analytics, driver management. Built for Saudi Arabia logistics operations."
        keywords="fleet management features, TMS features Saudi Arabia, shipment tracking, fuel management, driver management, logistics software KSA, transportation management"
        canonicalUrl="/features"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "SoftwareApplication",
          "name": "Logistiat Fleet Management Software",
          "applicationCategory": "BusinessApplication",
          "operatingSystem": "Web Browser",
          "description": "Complete TMS solution for Saudi Arabia with advanced fleet management features",
          "featureList": [
            "Shipment Tracking & Route Management",
            "Intelligent Trip Costing & VAT Management", 
            "Comprehensive Driver & Vehicle Management",
            "Smart Fuel Analytics",
            "Automated Odometer Sync",
            "Real-time Metrics Dashboard"
          ],
          "offers": {
            "@type": "Offer",
            "price": "2990",
            "priceCurrency": "SAR",
            "priceValidUntil": "2026-12-31",
            "availability": "https://schema.org/InStock",
            "url": "https://logistiat.app/pricing",
            "itemCondition": "https://schema.org/NewCondition",
            "shippingDetails": {
              "@type": "OfferShippingDetails",
              "shippingRate": {
                "@type": "MonetaryAmount",
                "value": "0",
                "currency": "SAR"
              },
              "shippingDestination": {
                "@type": "DefinedRegion",
                "addressCountry": "SA"
              },
              "deliveryTime": {
                "@type": "ShippingDeliveryTime",
                "handlingTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 0,
                  "maxValue": 0,
                  "unitCode": "DAY"
                },
                "transitTime": {
                  "@type": "QuantitativeValue",
                  "minValue": 0,
                  "maxValue": 0,
                  "unitCode": "DAY"
                }
              }
            },
            "hasMerchantReturnPolicy": {
              "@type": "MerchantReturnPolicy",
              "applicableCountry": "SA",
              "returnPolicyCategory": "https://schema.org/MerchantReturnFiniteReturnWindow",
              "merchantReturnDays": 30,
              "returnMethod": "https://schema.org/ReturnByMail",
              "returnFees": "https://schema.org/FreeReturn"
            }
          }
        }}
      />
      <main className="min-h-screen">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Link to="/" className="text-2xl font-bold text-gray-900">Logistiat</Link>
            </div>
            <div className="hidden md:flex space-x-8">
              <Link to="/features" className="text-blue-600 font-medium">Features</Link>
              <Link to="/pricing" className="text-gray-600 hover:text-gray-900">Pricing</Link>
              <Link to="/trip-costing" className="text-gray-600 hover:text-gray-900">Trip Costing</Link>
              <Link to="/contact" className="text-gray-600 hover:text-gray-900">Contact</Link>
              <Link to="/ar" className="text-gray-600 hover:text-gray-900">العربية</Link>
            </div>
            <div className="flex space-x-4">
              <Link
                to="/login"
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 font-medium"
              >
                Login
              </Link>
            </div>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Complete TMS Solution for Saudi Arabia
              </h1>
              <p className="text-xl md:text-2xl mb-8">
                Advanced fleet management with intelligent shipment tracking, automated costing, fuel analytics, and comprehensive driver management built specifically for KSA logistics operations.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 text-center"
                >
                  Start Free Trial
                </Link>
                <Link
                  to="#features"
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-blue-600 text-center"
                >
                  Explore Features
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="bg-white rounded-xl shadow-2xl p-4">
                <img
                  src="/images/dashboard-screenshot.png"
                  alt="Logistiat Fleet Management Dashboard"
                  className="rounded-lg w-full h-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section id="features" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Powerful Features for Modern Fleet Operations
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to manage your fleet efficiently, from real-time tracking to automated accounting integration.
            </p>
          </div>

          {/* Shipment Management Feature */}
          <div className="mb-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium mb-4">
                  🚛 Shipment Management
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Advanced Shipment Tracking & Route Management
                </h3>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">Multi-city route planning with automatic city detection for Saudi Arabia</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">Real-time status updates from dispatch to delivery completion</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">Automated vehicle and driver assignment with conflict detection</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-blue-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">Service requester management with client relationship tracking</p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="bg-white rounded-xl shadow-lg p-4">
                  <img
                    src="/images/shipment-tracking.png"
                    alt="Shipment Tracking Dashboard"
                    className="rounded-lg w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Trip Costing Feature */}
          <div className="mb-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="order-2 lg:order-1 relative">
                <div className="bg-white rounded-xl shadow-lg p-4">
                  <img
                    src="/images/trip-costing.png"
                    alt="Trip Costing Analytics"
                    className="rounded-lg w-full h-auto"
                  />
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium mb-4">
                  💰 Financial Management
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Intelligent Trip Costing & VAT Management
                </h3>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">Automated fuel cost allocation with bulk management capabilities</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">VAT-compliant calculations with automatic base/tax separation</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">Real-time profit margin analysis and cost breakdown</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-green-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">Automated journal entries for seamless accounting integration</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Driver Management Feature */}
          <div className="mb-20">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-4">
                  👥 Driver Operations
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Comprehensive Driver & Vehicle Management
                </h3>
                <div className="space-y-4 mb-6">
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">Complete driver profiles with performance tracking and ratings</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">Automated commission calculations and settlement management</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">Vehicle maintenance scheduling with odometer tracking</p>
                  </div>
                  <div className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-purple-600 rounded-full mt-2"></div>
                    <p className="text-gray-600">Arabic/English license plate matching and vehicle mapping</p>
                  </div>
                </div>
              </div>
              <div className="relative">
                <div className="bg-white rounded-xl shadow-lg p-4">
                  <img
                    src="/images/driver-management.png"
                    alt="Driver Management System"
                    className="rounded-lg w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advanced Features Grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Advanced Automation & Analytics
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Cutting-edge features that set Logistiat apart from traditional fleet management systems.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-6">
                <span className="text-2xl">⛽</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Smart Fuel Analytics</h3>
              <p className="text-gray-600 mb-4">Excel import with automatic vehicle matching, fraud detection, and bulk cost allocation across shipments.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Automatic Arabic/English plate matching</li>
                <li>• Fuel fraud detection algorithms</li>
                <li>• Bulk cost management dashboard</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-6">
                <span className="text-2xl">🔄</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Automated Odometer Sync</h3>
              <p className="text-gray-600 mb-4">Automatic odometer reading extraction from fuel transactions with intelligent validation and history tracking.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Fuel-to-odometer synchronization</li>
                <li>• Maintenance scheduling alerts</li>
                <li>• Historical reading validation</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-6">
                <span className="text-2xl">📊</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Real-time Metrics</h3>
              <p className="text-gray-600 mb-4">Live dashboard with profit margins, cost breakdowns, and performance analytics for data-driven decisions.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Revenue vs cost analysis</li>
                <li>• Driver performance metrics</li>
                <li>• Fleet utilization reports</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-6">
                <span className="text-2xl">🏦</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Accounting Integration</h3>
              <p className="text-gray-600 mb-4">Seamless integration with chart of accounts, automated journal entries, and VAT-compliant reporting.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Automated journal entries</li>
                <li>• VAT calculation & reporting</li>
                <li>• Expense categorization</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-6">
                <span className="text-2xl">🌐</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Bilingual Support</h3>
              <p className="text-gray-600 mb-4">Full Arabic and English interface with intelligent text processing and cultural localization for Saudi market.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Arabic/English UI switching</li>
                <li>• Cultural date formatting</li>
                <li>• Localized business rules</li>
              </ul>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-6">
                <span className="text-2xl">🔒</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Enterprise Security</h3>
              <p className="text-gray-600 mb-4">Multi-company isolation, role-based access control, and comprehensive audit trails for enterprise compliance.</p>
              <ul className="text-sm text-gray-500 space-y-1">
                <li>• Company data isolation</li>
                <li>• User role management</li>
                <li>• Activity audit logs</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Integration & Automation Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Built for Saudi Arabia's Logistics Industry
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Designed specifically for KSA regulations, business practices, and operational requirements.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl">🇸🇦</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">KSA Compliance</h3>
              <p className="text-gray-600">VAT regulations, Arabic documentation, and local business practice compliance built-in.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Rapid Deployment</h3>
              <p className="text-gray-600">Quick setup with pre-configured Saudi cities, truck types, and business workflows.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-white text-2xl">📱</span>
              </div>
              <h3 className="text-xl font-semibold mb-3">Mobile Ready</h3>
              <p className="text-gray-600">Responsive design optimized for tablets and mobile devices used by drivers and dispatchers.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Fleet Operations?</h2>
          <p className="text-xl mb-8">
            Join leading logistics companies in Saudi Arabia who have streamlined their operations with Logistiat's comprehensive TMS solution.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 text-lg"
            >
              Start Your Free Trial
            </Link>
            <Link
              to="/pricing"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-600 text-lg"
            >
              View Pricing Plans
            </Link>
          </div>
          <p className="text-blue-200 mt-6">
            No setup fees • 30-day free trial • Cancel anytime
          </p>
        </div>
      </section>
      </main>
    </>
  );
};

export default FeaturesPage;